import { isValidElement, useEffect, useMemo, useState, type ReactNode } from "react";
import { Button, Card, DatePicker, Input, Select, Space, Typography } from "antd";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import {
    DELIVERY_STATE,
    DELIVERY_TYPE,
    PAYMENT_TYPE_EXCLUDE_SCHEDULE,
    RECEIVE_STATE,
    REFUND_STATE,
    SCHEDULE_STATE,
    S_INVOICE_STATE,
    TRADE_ORDER_STATE
} from "@zhihaoscm/meta";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

export type ProductDetailTabRecord = Record<string, unknown> & {
    id?: string | number;
    productConsignment?: Record<string, any>;
};

export interface ProductDetailTabProps {
    data?: ProductDetailTabRecord;
    items?: ProductDetailTabRecord[];
    children?: ReactNode;
}

export type ProductDetailFilterValue = string | number | Array<string | number> | undefined;
export type ProductDetailFilterValues = Record<string, ProductDetailFilterValue>;

interface ProductDetailFilterField {
    key: string;
    type: "input" | "select" | "date-range";
    placeholder?: string;
    startPlaceholder?: string;
    endPlaceholder?: string;
    width: number;
    multiple?: boolean;
    options?: Array<{ label: string; value: string | number }>;
}

const metaOptions = (options: Array<{ label: string; value: string | number }>) =>
    options.map(option => ({ label: option.label, value: option.value }));

/** 使用 Element Plus RefreshLeft 的同款轮廓，保证重置按钮与旧版图标方向和笔画一致。 */
function ProductDetailResetIcon() {
    return (
        <svg aria-hidden="true" viewBox="0 0 1024 1024">
            <path
                d="M289.088 296.704h92.992a32 32 0 0 1 0 64H232.96a32 32 0 0 1-32-32V179.712a32 32 0 0 1 64 0v50.56a384 384 0 0 1 643.84 282.88 384 384 0 0 1-383.936 384 384 384 0 0 1-384-384h64a320 320 0 1 0 640 0 320 320 0 0 0-555.712-216.448z"
                fill="currentColor"
            />
        </svg>
    );
}

/** 使用 Element Plus ArrowDown 的同款轮廓，统一单选和多选下拉的箭头。 */
function ProductDetailSelectIcon() {
    return (
        <svg aria-hidden="true" viewBox="0 0 1024 1024">
            <path
                d="M831.872 340.864 512 652.672 192.128 340.864a30.59 30.59 0 0 0-42.752 0 29.12 29.12 0 0 0 0 41.6L489.664 714.24a32 32 0 0 0 44.672 0l340.288-331.712a29.12 29.12 0 0 0 0-41.728 30.59 30.59 0 0 0-42.752 0z"
                fill="currentColor"
            />
        </svg>
    );
}

/** 逐页复刻旧 Vue zh-table 的搜索配置，字段名直接对应后端分页接口参数。 */
const PRODUCT_DETAIL_FILTER_FIELDS: Record<string, ProductDetailFilterField[]> = {
    订单明细: [
        { key: "id", type: "input", placeholder: "请输入订单编号搜索", width: 130 },
        { key: "keyword", type: "input", placeholder: "请输入采购账号信息搜索", width: 154 },
        {
            key: "date",
            type: "date-range",
            startPlaceholder: "下单开始日期",
            endPlaceholder: "下单结束日期",
            width: 210
        },
        {
            key: "states",
            type: "select",
            placeholder: "请选择状态",
            width: 110,
            multiple: true,
            options: metaOptions(TRADE_ORDER_STATE.options.filter(option => ![7, 8].includes(Number(option.value))))
        }
    ],
    提货明细: [
        { key: "orderId", type: "input", placeholder: "请输入订单编号", width: 106 },
        { key: "scheduleId", type: "input", placeholder: "请输入排期编号", width: 106 },
        { key: "deliveryId", type: "input", placeholder: "请输入提货编号", width: 106 },
        {
            key: "states",
            type: "select",
            placeholder: "请选择提货状态",
            width: 134,
            multiple: true,
            options: metaOptions(DELIVERY_STATE.options)
        },
        {
            key: "type",
            type: "select",
            placeholder: "请选择类型",
            width: 110,
            options: metaOptions(DELIVERY_TYPE.options)
        }
    ],
    排期列表: [
        { key: "id", type: "input", placeholder: "请输入排期编号搜索", width: 130 },
        { key: "orderId", type: "input", placeholder: "请输入订单编号搜索", width: 130 },
        { key: "customerKey", type: "input", placeholder: "请输采购用户账号信息搜索", width: 166 },
        { key: "shipKey", type: "input", placeholder: "请输入船舶搜索", width: 106 },
        {
            key: "states",
            type: "select",
            placeholder: "请选择状态",
            width: 110,
            multiple: true,
            options: metaOptions(SCHEDULE_STATE.options)
        }
    ],
    收款费用明细: [
        { key: "id", type: "input", placeholder: "请输入收款编号搜索", width: 130 },
        { key: "orderId", type: "input", placeholder: "请输入订单编号搜索", width: 130 },
        { key: "keyword", type: "input", placeholder: "请输入采购商账号信息搜索", width: 166 },
        {
            key: "createDate",
            type: "date-range",
            startPlaceholder: "创建开始日期",
            endPlaceholder: "创建结束日期",
            width: 210
        },
        {
            key: "paymentTypes",
            type: "select",
            placeholder: "请选择费用类型",
            width: 134,
            multiple: true,
            options: metaOptions(PAYMENT_TYPE_EXCLUDE_SCHEDULE.options)
        },
        {
            key: "states",
            type: "select",
            placeholder: "请选择状态",
            width: 110,
            multiple: true,
            options: metaOptions(RECEIVE_STATE.options.filter(option => Number(option.value) !== 3))
        }
    ],
    退款费用明细: [
        { key: "id", type: "input", placeholder: "请输入退款编号搜索", width: 130 },
        { key: "orderId", type: "input", placeholder: "请输入订单编号搜索", width: 130 },
        { key: "keyword", type: "input", placeholder: "请输入采购商账号信息搜索", width: 166 },
        {
            key: "createDate",
            type: "date-range",
            startPlaceholder: "创建开始日期",
            endPlaceholder: "创建结束日期",
            width: 210
        },
        {
            key: "states",
            type: "select",
            placeholder: "请选择状态",
            width: 110,
            multiple: true,
            options: metaOptions(REFUND_STATE.options)
        }
    ],
    发票明细: [
        { key: "invoiceId", type: "input", placeholder: "请输入开票编号搜索", width: 130 },
        { key: "orderId", type: "input", placeholder: "请输入订单编号搜索", width: 130 },
        { key: "purchaserInfo", type: "input", placeholder: "请输入采购用户账号信息搜索", width: 178 },
        {
            key: "date",
            type: "date-range",
            startPlaceholder: "创建开始日期",
            endPlaceholder: "创建结束日期",
            width: 210
        },
        {
            key: "state",
            type: "select",
            placeholder: "请选择状态",
            width: 110,
            options: metaOptions(S_INVOICE_STATE.options)
        }
    ]
};

/** 把旧版日期区间字段转换为后端接收的日界时间参数。 */
export function withProductDetailDateRange(values: ProductDetailFilterValues, key: "date" | "createDate") {
    const range = values[key];
    const params = { ...values };
    delete params[key];
    if (Array.isArray(range) && range.length === 2) {
        params.beginTime = `${range[0]}T00:00:00`;
        params.endTime = `${range[1]}T23:59:59`;
    }
    return params;
}

/** 详情页子 Tab 沿用 Vue 的 section-card 容器，避免迁移占位面板改变页签的层级和间距。 */
export function ProductDetailTabSection({
    title,
    children,
    className = "",
    showTitle = true
}: {
    title: string;
    children: ReactNode;
    className?: string;
    showTitle?: boolean;
}) {
    return (
        <section aria-label={title} className={`section-card product-detail-tab-section ${className}`.trim()}>
            {showTitle ? <div className="section-title">{title}</div> : null}
            {children}
        </section>
    );
}

/** 旧 Vue zh-table 的空态仍保留表头和最小高度，避免显示通用迁移字段或备注输入框。 */
export function ProductDetailTabTable({
    columns,
    data = [],
    ariaLabel,
    total,
    onSearch,
    onCreate
}: {
    columns: TableColumnProps<ProductDetailTabRecord>[];
    data?: ProductDetailTabRecord[];
    ariaLabel: string;
    total?: number;
    onSearch?: (values: ProductDetailFilterValues) => void;
    onCreate?: () => void;
}) {
    const searchFields = useMemo(() => PRODUCT_DETAIL_FILTER_FIELDS[ariaLabel] || [], [ariaLabel]);
    const [filterValues, setFilterValues] = useState<ProductDetailFilterValues>({});
    useEffect(() => setFilterValues({}), [ariaLabel]);
    const dateValues = useMemo(
        () =>
            Object.fromEntries(
                searchFields
                    .filter(field => field.type === "date-range")
                    .map(field => {
                        const value = filterValues[field.key];
                        return [
                            field.key,
                            Array.isArray(value) && value.length === 2
                                ? ([dayjs(String(value[0])), dayjs(String(value[1]))] as [Dayjs, Dayjs])
                                : null
                        ];
                    })
            ),
        [filterValues, searchFields]
    );
    const widthMaps: Record<string, Record<string, number>> = {
        排期列表: {
            排期日期: 120,
            排期编号: 150,
            订单编号: 150,
            采购用户: 278,
            "预估提货吨数(吨)": 148,
            船舶: 190,
            运单: 190,
            收货信息: 206,
            排期状态: 92,
            操作: 120
        },
        提货明细: {
            提货编号: 180,
            提货日期: 190,
            结算金额: 170,
            "提货吨位（吨）": 170,
            "结算单价（元/吨）": 180,
            状态: 170,
            关联排期: 180,
            关联订单: 180,
            类型: 170,
            创建时间: 190,
            操作: 240
        },
        订单明细: {
            订单编号: 148,
            采购用户: 120,
            "交易单价(元/吨)": 148,
            "采购吨数(吨)": 148,
            "预估金额(元)": 148,
            "实收款(元)": 148,
            下单时间: 154,
            状态: 120
        },
        收款费用明细: {
            收款编号: 170,
            关联订单: 170,
            采购用户: 250,
            费用类型: 92,
            费用说明: 160,
            "支付金额(元)": 120,
            支付时间: 170,
            付款说明: 200,
            确认支付凭证时间: 188,
            创建时间: 188,
            支付状态: 120,
            操作: 240
        },
        退款费用明细: {
            退款编号: 160,
            关联订单: 170,
            采购用户: 280,
            退款类型: 106,
            退款说明: 160,
            "退款金额（元）": 130,
            退款时间: 188,
            创建时间: 170,
            状态: 92,
            操作: 120
        },
        发票明细: {
            开票编号: 150,
            订单编号: 150,
            采购用户: 280,
            "开票金额(元)": 130,
            创建时间: 154,
            开票日期: 110,
            状态: 78,
            操作: 120
        }
    };
    const visualColumns = columns.map(column => {
        const label = typeof column.label === "string" ? column.label : "";
        const align = /金额|单价|吨数|吨位/.test(label) ? "right" : column.align;
        return {
            ...column,
            width: widthMaps[ariaLabel]?.[label] ?? column.width,
            align,
            headerAlign: align,
            fixed: label === "操作" && ariaLabel !== "订单明细" ? "right" : column.fixed
        } as TableColumnProps<ProductDetailTabRecord>;
    });
    return (
        <div aria-label={ariaLabel} className="product-detail-tab-table">
            <div className="product-detail-tab-toolbar" aria-label={`${ariaLabel}筛选`}>
                {ariaLabel === "排期列表" && onCreate ? (
                    <Button
                        className="product-detail-tab-create product-detail-tab-create--schedule"
                        size="small"
                        type="primary"
                        onClick={onCreate}
                    >
                        新增排期
                    </Button>
                ) : null}
                {ariaLabel === "退款费用明细" && onCreate ? (
                    <Button className="product-detail-tab-create" size="small" type="primary" onClick={onCreate}>
                        新增
                    </Button>
                ) : null}
                {searchFields.map(field => {
                    const style = { width: field.width };
                    if (field.type === "input") {
                        return (
                            <Input
                                aria-label={field.placeholder}
                                className="product-detail-tab-filter-control"
                                key={field.key}
                                placeholder={field.placeholder}
                                size="small"
                                style={style}
                                value={String(filterValues[field.key] || "")}
                                onChange={event =>
                                    setFilterValues(values => ({
                                        ...values,
                                        [field.key]: event.target.value || undefined
                                    }))
                                }
                                onPressEnter={() => onSearch?.(filterValues)}
                            />
                        );
                    }
                    if (field.type === "date-range") {
                        return (
                            <DatePicker.RangePicker
                                allowClear
                                aria-label={`${field.startPlaceholder}-${field.endPlaceholder}`}
                                className="product-detail-tab-filter-control"
                                format="YYYY-MM-DD"
                                key={field.key}
                                placeholder={[field.startPlaceholder || "开始日期", field.endPlaceholder || "结束日期"]}
                                separator="-"
                                size="small"
                                style={style}
                                suffixIcon={null}
                                value={dateValues[field.key]}
                                onChange={(_, dates) =>
                                    setFilterValues(values => ({
                                        ...values,
                                        [field.key]: dates[0] && dates[1] ? dates : undefined
                                    }))
                                }
                            />
                        );
                    }
                    return (
                        <Select
                            allowClear
                            aria-label={field.placeholder}
                            className="product-detail-tab-filter-control"
                            key={field.key}
                            maxTagCount="responsive"
                            mode={field.multiple ? "multiple" : undefined}
                            options={field.options}
                            placeholder={field.placeholder}
                            size="small"
                            style={style}
                            suffixIcon={<ProductDetailSelectIcon />}
                            value={filterValues[field.key]}
                            onChange={value => setFilterValues(values => ({ ...values, [field.key]: value }))}
                        />
                    );
                })}
                <Button
                    className="product-detail-tab-search"
                    icon={<i aria-hidden="true" className="ztable-iconfont icon-zhihao-table-search" />}
                    size="small"
                    type="primary"
                    onClick={() => onSearch?.(filterValues)}
                >
                    搜索
                </Button>
                <Button
                    size="small"
                    className="product-detail-tab-reset"
                    aria-label={`${ariaLabel}重置`}
                    icon={<ProductDetailResetIcon />}
                    onClick={() => {
                        setFilterValues({});
                        onSearch?.({});
                    }}
                />
            </div>
            <ZhTable<ProductDetailTabRecord>
                columns={visualColumns}
                data={data}
                pagination={false}
                rowKey={row => String(row.id ?? "row")}
                isShowResetBtn={false}
                isShowSearchBtn={false}
            />
            {data.length > 0 ? (
                <div className="product-detail-tab-pagination" aria-label={`${ariaLabel}分页`}>
                    <span>共{total ?? data.length}条数据</span>
                    <span className="product-detail-tab-page-size">10条/页⌄</span>
                    <Button size="small" disabled>
                        ‹
                    </Button>
                    <Button size="small" type="primary">
                        1
                    </Button>
                    {(total ?? data.length) > 10 ? <Button size="small">2</Button> : null}
                    <Button size="small">›</Button>
                    <span>前往</span>
                    <span className="product-detail-tab-page-number">1</span>
                    <span>页</span>
                </div>
            ) : null}
        </div>
    );
}

/** 详情页的统计卡片保持旧 Vue 的横向四列网格，不再输出迁移组件的英文字段名。 */
export function ProductDetailTabMetrics({
    items
}: {
    items: Array<{ label: string; value: ReactNode; type?: "num" | "money" | "bi" }>;
}) {
    return (
        <div aria-label="详情统计" className="admin-trade-stat-cards product-detail-tab-metrics">
            {items.map((item, index) => (
                <Card
                    className={`admin-trade-stat-card admin-trade-stat-card--${item.type || "num"} admin-trade-stat-card--metric-${index}`}
                    key={item.label}
                    size="small"
                >
                    <Typography.Text className="admin-trade-stat-card__label">{item.label}</Typography.Text>
                    <Typography.Title
                        className={`admin-trade-stat-card__value${index === items.length - 1 ? " admin-trade-stat-card__value--primary" : ""}`}
                        level={3}
                    >
                        {item.value}
                    </Typography.Title>
                </Card>
            ))}
        </div>
    );
}

/** 费用页签使用旧 Vue 的收款/退款内嵌页签，操作按钮只在真实业务有回调时出现。 */
export function ProductDetailSubTabs({ active, onChange }: { active: string; onChange: (value: string) => void }) {
    return (
        <div className="admin-detail-tabs product-detail-sub-tabs" role="tablist" aria-label="费用明细子页签">
            {["收款", "退款"].map(label => (
                <Button
                    key={label}
                    aria-selected={active === label}
                    className={active === label ? "is-active" : ""}
                    role="tab"
                    type="text"
                    onClick={() => onChange(label)}
                >
                    {label}
                </Button>
            ))}
        </div>
    );
}

export const emptyProductDetailColumns = (labels: string[]): TableColumnProps<ProductDetailTabRecord>[] =>
    labels.map(label => ({
        key: label,
        label,
        prop: label,
        render: ({ row }) => {
            const value = row[label];
            // 业务页负责提供 Link/Button 等真实交互节点；普通编号必须保持旧 Vue 的正文颜色。
            if (isValidElement(value) || Array.isArray(value)) return value as ReactNode;
            return String(value ?? "--");
        }
    }));

export function ProductDetailTabActions({ children }: { children?: ReactNode }) {
    return children ? <Space className="product-detail-tab-actions">{children}</Space> : null;
}
